import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Alert, Stack, Card, CardContent,
  CardActions, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, Select, MenuItem, InputLabel, Tab, Tabs,
  Grid, LinearProgress, Tooltip, Divider, IconButton,
} from '@mui/material';
import {
  Event as EventIcon, Loop as LoopIcon, VolunteerActivism,
  Refresh, PersonAdd, OpenInNew, ContentCopy, Check,
} from '@mui/icons-material';
import api from '../../services/api';

// ─── Types ───────────────────────────────────────────────
interface Evento {
  id: string | number;
  nome: string;
  descricao: string | null;
  data_inicio: string | null;
  data_fim: string | null;
  local: string | null;
  estado: string;
  capacidade: number | null;
  inscritos: number;
}

interface BilheteTipo {
  id: string | number;
  nome: string;
  preco: number;
  disponivel: number | null;
}

interface WagPlano {
  id: string;
  codigo: string;
  nome: string;
  descr: string | null;
  precoMensal: number;
  precoAnual: number;
  maxAgencias: number;
  maxServicos: number;
  maxOperadores: number;
  smsEnabled: string;
}

interface Campanha {
  id: string;
  titulo: string;
  descricao: string | null;
  meta: number | null;
  valorArrecadado: number;
  dataFim: string | null;
  estado: string;
  slug: string | null;
  totalDoadores: number;
}

// ─── Helpers ─────────────────────────────────────────────
const fmt = (n: number) =>
  new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(n);

const fmtDate = (dt: string | null) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('pt-PT', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Tab panel wrapper ────────────────────────────────────
function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return value === index ? <Box>{children}</Box> : null;
}

// ══════════════════════════════════════════════════════════
// TAB 0: EVENTOS
// ══════════════════════════════════════════════════════════
function EventosTab() {
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvento, setSelectedEvento] = useState<Evento | null>(null);
  const [bilhetesTipo, setBilhetesTipo] = useState<BilheteTipo[]>([]);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [form, setForm] = useState({
    bilheteTipoId: '',
    participanteNome: '',
    participanteEmail: '',
    participanteTelefone: '',
    participantePerfil: '',
    participanteModalidade: '',
    participanteResidencia: '',
    participantePais: 'CV',
    participanteInstituicao: '',
    montante: '',
  });

  const fetchEventos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/agente/events');
      setEventos(res.data?.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEventos(); }, [fetchEventos]);

  const openDialog = async (evento: Evento) => {
    setSelectedEvento(evento);
    setForm({ bilheteTipoId: '', participanteNome: '', participanteEmail: '', participanteTelefone: '',
      participantePerfil: '', participanteModalidade: '', participanteResidencia: '', participantePais: 'CV',
      participanteInstituicao: '', montante: '' });
    setSuccessMsg(null);
    setDialogOpen(true);
    setLoadingTipos(true);
    try {
      const res = await api.get(`/events/${evento.id}/bilhetes-tipo`);
      setBilhetesTipo(res.data?.data || []);
    } catch {
      setBilhetesTipo([]);
    } finally {
      setLoadingTipos(false);
    }
  };

  const handleTipoChange = (tipoId: string) => {
    const tipo = bilhetesTipo.find(t => String(t.id) === tipoId);
    setForm(f => ({ ...f, bilheteTipoId: tipoId, montante: tipo ? String(tipo.preco) : '' }));
  };

  const handleSubmit = async () => {
    if (!selectedEvento) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post(`/agente/events/${selectedEvento.id}/registar`, {
        ...form,
        montante: parseFloat(form.montante),
      });
      setSuccessMsg(`Participante inscrito! Bilhete: ${res.data?.data?.bilheteId?.slice(0, 8).toUpperCase()}`);
      fetchEventos();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao registar participante');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" fontWeight={700}>
          Eventos disponíveis para inscrição
        </Typography>
        <IconButton onClick={fetchEventos} size="small" color="primary">
          <Refresh fontSize="small" />
        </IconButton>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {eventos.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <EventIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">Nenhum evento disponível de momento.</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {eventos.map(ev => {
            const vagas = ev.capacidade ? ev.capacidade - ev.inscritos : null;
            const pct = ev.capacidade ? Math.min(100, (ev.inscritos / ev.capacidade) * 100) : null;
            return (
              <Grid item xs={12} sm={6} md={4} key={ev.id}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      {ev.nome}
                    </Typography>
                    {ev.data_inicio && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {fmtDate(ev.data_inicio)}{ev.data_fim && ev.data_fim !== ev.data_inicio ? ` – ${fmtDate(ev.data_fim)}` : ''}
                      </Typography>
                    )}
                    {ev.local && (
                      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        {ev.local}
                      </Typography>
                    )}
                    {pct !== null && (
                      <Box mt={1}>
                        <Stack direction="row" justifyContent="space-between" mb={0.25}>
                          <Typography variant="caption" color="text.secondary">{ev.inscritos} inscritos</Typography>
                          <Typography variant="caption" color={vagas === 0 ? 'error' : 'text.secondary'}>
                            {vagas === 0 ? 'Esgotado' : `${vagas} vagas`}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={pct}
                          color={pct >= 90 ? 'error' : pct >= 70 ? 'warning' : 'primary'}
                          sx={{ height: 4, borderRadius: 2 }}
                        />
                      </Box>
                    )}
                  </CardContent>
                  <CardActions sx={{ pt: 0 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PersonAdd />}
                      onClick={() => openDialog(ev)}
                      disabled={vagas === 0}
                      fullWidth
                      sx={{ borderRadius: 1.5 }}
                    >
                      Inscrever Participante
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>
          Inscrever Participante{selectedEvento ? ` — ${selectedEvento.nome}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {successMsg ? (
            <Alert severity="success" icon={<Check />}>{successMsg}</Alert>
          ) : (
            <Stack spacing={2} mt={0.5}>
              {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

              {loadingTipos ? (
                <CircularProgress size={24} />
              ) : (
                <FormControl size="small" fullWidth required>
                  <InputLabel>Tipo de bilhete</InputLabel>
                  <Select
                    label="Tipo de bilhete"
                    value={form.bilheteTipoId}
                    onChange={e => handleTipoChange(e.target.value)}
                  >
                    {bilhetesTipo.map(t => (
                      <MenuItem key={t.id} value={String(t.id)}>
                        {t.nome} — {fmt(t.preco)}
                      </MenuItem>
                    ))}
                    {bilhetesTipo.length === 0 && (
                      <MenuItem disabled value="">
                        Nenhum tipo disponível
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

              <TextField
                size="small"
                label="Nome completo"
                required
                value={form.participanteNome}
                onChange={e => setForm(f => ({ ...f, participanteNome: e.target.value }))}
              />
              <TextField
                size="small"
                label="Email"
                type="email"
                required
                value={form.participanteEmail}
                onChange={e => setForm(f => ({ ...f, participanteEmail: e.target.value }))}
              />
              <TextField
                size="small"
                label="Telefone"
                value={form.participanteTelefone}
                onChange={e => setForm(f => ({ ...f, participanteTelefone: e.target.value }))}
              />
              <TextField
                size="small"
                label="Instituição / Empresa"
                value={form.participanteInstituicao}
                onChange={e => setForm(f => ({ ...f, participanteInstituicao: e.target.value }))}
              />
              <TextField
                size="small"
                label="Montante (CVE)"
                type="number"
                required
                value={form.montante}
                onChange={e => setForm(f => ({ ...f, montante: e.target.value }))}
                helperText="Será debitado do seu saldo float"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {successMsg ? 'Fechar' : 'Cancelar'}
          </Button>
          {!successMsg && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || !form.bilheteTipoId || !form.participanteNome || !form.participanteEmail || !form.montante}
            >
              {submitting ? <CircularProgress size={18} /> : 'Confirmar Inscrição'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

// ══════════════════════════════════════════════════════════
// TAB 1: WAG
// ══════════════════════════════════════════════════════════
function WagTab() {
  const [planos, setPlanos] = useState<WagPlano[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlano, setSelectedPlano] = useState<WagPlano | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [form, setForm] = useState({ tenantId: '', clienteNome: '', clienteEmail: '', observacoes: '' });

  useEffect(() => {
    api.get('/agente/wag/planos')
      .then(res => setPlanos(res.data?.data || []))
      .catch(err => setError(err.response?.data?.error || 'Erro ao carregar planos'))
      .finally(() => setLoading(false));
  }, []);

  const openDialog = (plano: WagPlano) => {
    setSelectedPlano(plano);
    setForm({ tenantId: '', clienteNome: '', clienteEmail: '', observacoes: '' });
    setSuccessMsg(null);
    setError(null);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/agente/wag/referir', form);
      setSuccessMsg('Cliente WAG associado! A comissão será registada na próxima subscrição paga.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao associar cliente');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;

  return (
    <>
      <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
        Planos WAG — Fila de Atendimento
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Refira clientes para o WAG e ganhe comissão recorrente em cada renovação de subscrição.
      </Typography>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2}>
        {planos.map(plano => (
          <Grid item xs={12} sm={6} md={4} key={plano.id}>
            <Card
              variant="outlined"
              sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column',
                    border: plano.codigo === 'PRO' ? '2px solid' : undefined,
                    borderColor: plano.codigo === 'PRO' ? 'primary.main' : undefined }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Typography variant="subtitle2" fontWeight={800}>{plano.nome}</Typography>
                  {plano.codigo === 'PRO' && (
                    <Chip label="Popular" size="small" color="primary" sx={{ fontSize: 10, height: 18 }} />
                  )}
                </Stack>
                <Typography variant="h6" fontWeight={800} color="primary.main">
                  {fmt(plano.precoMensal)}
                  <Typography component="span" variant="caption" color="text.secondary" ml={0.5}>/mês</Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  ou {fmt(plano.precoAnual)}/ano
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={0.5}>
                  <Typography variant="caption">👥 Até {plano.maxAgencias} agência(s)</Typography>
                  <Typography variant="caption">📋 Até {plano.maxServicos} serviço(s)</Typography>
                  <Typography variant="caption">👤 Até {plano.maxOperadores} operador(es)</Typography>
                  {plano.smsEnabled === 'S' || plano.smsEnabled === '1' ? (
                    <Typography variant="caption">✉️ SMS incluído</Typography>
                  ) : null}
                </Stack>
              </CardContent>
              <CardActions sx={{ pt: 0 }}>
                <Button
                  size="small"
                  variant={plano.codigo === 'PRO' ? 'contained' : 'outlined'}
                  startIcon={<PersonAdd />}
                  onClick={() => openDialog(plano)}
                  fullWidth
                  sx={{ borderRadius: 1.5 }}
                >
                  Referir Cliente
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>
          Referir Cliente WAG{selectedPlano ? ` — ${selectedPlano.nome}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {successMsg ? (
            <Alert severity="success" icon={<Check />}>{successMsg}</Alert>
          ) : (
            <Stack spacing={2} mt={0.5}>
              {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
              <Typography variant="body2" color="text.secondary">
                Introduza o ID do tenant WAG do cliente. Quando o cliente subscrever, a comissão será registada automaticamente.
              </Typography>
              <TextField
                size="small"
                label="ID do Tenant WAG"
                required
                value={form.tenantId}
                onChange={e => setForm(f => ({ ...f, tenantId: e.target.value }))}
                helperText="Identificador único do cliente no sistema WAG"
              />
              <TextField
                size="small"
                label="Nome do cliente"
                value={form.clienteNome}
                onChange={e => setForm(f => ({ ...f, clienteNome: e.target.value }))}
              />
              <TextField
                size="small"
                label="Email do cliente"
                type="email"
                value={form.clienteEmail}
                onChange={e => setForm(f => ({ ...f, clienteEmail: e.target.value }))}
              />
              <TextField
                size="small"
                label="Observações"
                multiline
                rows={2}
                value={form.observacoes}
                onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {successMsg ? 'Fechar' : 'Cancelar'}
          </Button>
          {!successMsg && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || !form.tenantId}
            >
              {submitting ? <CircularProgress size={18} /> : 'Associar Cliente'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

// ══════════════════════════════════════════════════════════
// TAB 2: IFUNDHERE
// ══════════════════════════════════════════════════════════
function IFundHereTab() {
  const [campanhas, setCampanhas] = useState<Campanha[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCampanha, setSelectedCampanha] = useState<Campanha | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [linkResult, setLinkResult] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ clienteNome: '', clienteEmail: '', observacoes: '' });

  useEffect(() => {
    api.get('/agente/ifundhere/campanhas')
      .then(res => setCampanhas(res.data?.data || []))
      .catch(err => setError(err.response?.data?.error || 'Erro ao carregar campanhas'))
      .finally(() => setLoading(false));
  }, []);

  const openDialog = (campanha: Campanha) => {
    setSelectedCampanha(campanha);
    setForm({ clienteNome: '', clienteEmail: '', observacoes: '' });
    setLinkResult(null);
    setError(null);
    setCopied(false);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedCampanha) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post('/agente/ifundhere/referir', {
        campanhaId: selectedCampanha.id,
        ...form,
      });
      setLinkResult(res.data?.data?.linkCaptacao || '');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao associar campanha');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = () => {
    if (linkResult) {
      navigator.clipboard.writeText(linkResult).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;

  return (
    <>
      <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
        Campanhas iFundHere — Captação de Doações
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Associe-se a campanhas e partilhe o seu link de captação. Ganha comissão em cada doação recebida.
      </Typography>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {campanhas.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <VolunteerActivism sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">Nenhuma campanha ativa de momento.</Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {campanhas.map(c => {
            const pct = c.meta ? Math.min(100, (c.valorArrecadado / c.meta) * 100) : null;
            return (
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      {c.titulo}
                    </Typography>
                    {c.descricao && (
                      <Typography variant="caption" color="text.secondary" display="block" mb={1}
                        sx={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {c.descricao}
                      </Typography>
                    )}
                    {c.dataFim && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Termina: {fmtDate(c.dataFim)}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} mt={1} alignItems="center">
                      <VolunteerActivism sx={{ fontSize: 14, color: 'warning.main' }} />
                      <Typography variant="caption" fontWeight={700}>{fmt(c.valorArrecadado)}</Typography>
                      {c.meta && (
                        <Typography variant="caption" color="text.secondary">de {fmt(c.meta)}</Typography>
                      )}
                    </Stack>
                    {pct !== null && (
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        color="warning"
                        sx={{ height: 4, borderRadius: 2, mt: 0.75 }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {c.totalDoadores} doador{c.totalDoadores !== 1 ? 'es' : ''}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ pt: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<OpenInNew />}
                      onClick={() => openDialog(c)}
                      fullWidth
                      sx={{ borderRadius: 1.5 }}
                    >
                      Gerar Link
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>
          Captação iFundHere{selectedCampanha ? ` — ${selectedCampanha.titulo}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {linkResult !== null ? (
            <Stack spacing={2}>
              <Alert severity="success">
                Associação registada! Partilhe o link abaixo para captar doações.
              </Alert>
              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Link de captação
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" fontFamily="monospace" sx={{ flex: 1, wordBreak: 'break-all' }}>
                    {linkResult}
                  </Typography>
                  <Tooltip title={copied ? 'Copiado!' : 'Copiar'}>
                    <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
                      {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            </Stack>
          ) : (
            <Stack spacing={2} mt={0.5}>
              {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
              <Typography variant="body2" color="text.secondary">
                Opcionalmente, registe os dados do seu contacto para acompanhamento interno.
              </Typography>
              <TextField
                size="small"
                label="Nome do contacto (opcional)"
                value={form.clienteNome}
                onChange={e => setForm(f => ({ ...f, clienteNome: e.target.value }))}
              />
              <TextField
                size="small"
                label="Email do contacto (opcional)"
                type="email"
                value={form.clienteEmail}
                onChange={e => setForm(f => ({ ...f, clienteEmail: e.target.value }))}
              />
              <TextField
                size="small"
                label="Observações"
                multiline
                rows={2}
                value={form.observacoes}
                onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {linkResult !== null ? 'Fechar' : 'Cancelar'}
          </Button>
          {linkResult === null && (
            <Button variant="contained" color="warning" onClick={handleSubmit} disabled={submitting}>
              {submitting ? <CircularProgress size={18} /> : 'Gerar Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

// ══════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════
export default function AgenteServicos() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={800} mb={0.5}>
        Serviços Pagali
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2.5}>
        Inscreva participantes em eventos, refira clientes WAG ou associe-se a campanhas de captação iFundHere.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab
            label="Eventos"
            icon={<EventIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            sx={{ fontSize: 13, minHeight: 48 }}
          />
          <Tab
            label="WAG"
            icon={<LoopIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            sx={{ fontSize: 13, minHeight: 48 }}
          />
          <Tab
            label="iFundHere"
            icon={<VolunteerActivism sx={{ fontSize: 18 }} />}
            iconPosition="start"
            sx={{ fontSize: 13, minHeight: 48 }}
          />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}><EventosTab /></TabPanel>
      <TabPanel value={tab} index={1}><WagTab /></TabPanel>
      <TabPanel value={tab} index={2}><IFundHereTab /></TabPanel>
    </Box>
  );
}
